import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const HVACTieredProposal = () => {
  useEffect(() => {
    analytics.track('Template Page View', { template: 'HVAC Tiered Proposal' });
  }, []);

  return (
    <SEOPageLayout
      title="Free HVAC Proposal Template - Tiered Options (Good/Better/Best) | QuoteTier"
      description="Download this free HVAC proposal template. Built for high-efficiency system sales with clear SEER ratings, rebates, and monthly payment options."
      canonical="/templates/tiered-hvac-proposal-template"
      ogTitle="The Ultimate HVAC Proposal Template"
      ogDescription="Don't quote just one system. Give them 3 options and watch your margins grow. Ready-to-use template for HVAC pros."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Free <span className="text-primary font-bold">HVAC Proposal</span> Template
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Show a 14 SEER budget unit, a 16 SEER high-efficiency model, and a 20+ SEER variable-speed setup on the same quote — rebates and all.
            </p>
            <div className="max-w-2xl mx-auto mb-10 p-5 border border-gray-200 rounded-lg bg-gray-50 text-left">
              <p className="font-jetbrains text-sm md:text-base text-gray-700 leading-relaxed">
                HVAC buyers comparing three flat-price quotes from three contractors almost always pick the cheapest. But if all three options are yours — budget, high-efficiency, and premium variable-speed — the middle tier wins most of the time. The anchoring is doing the work.
              </p>
            </div>
            <div className="font-instrument mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center mb-3">
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Use This Template Free
                </Button>
              </Link>
            </div>
            <p className="font-jetbrains text-sm text-gray-500 mb-6">Free account — no credit card required.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Sample Tiers</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
             {/* Simple representation of the template tiers */}
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Silver (14 SEER)</CardTitle><CardDescription>The Budget Option</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Standard Single-Stage Unit</li>
                      <li>Basic Thermostat</li>
                      <li>1-Year Labor Warranty</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black bg-black text-white">
              <CardHeader><CardTitle className="font-instrument text-xl text-white">Gold (16 SEER)</CardTitle><CardDescription className="text-gray-300">Best Seller</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-300 list-disc list-inside">
                      <li>High-Efficiency Two-Stage Unit</li>
                      <li>WiFi Smart Thermostat</li>
                      <li>5-Year Labor Warranty</li>
                      <li>Air Scrubber Included</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Platinum (20+ SEER)</CardTitle><CardDescription>Ultimate Comfort</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Variable Speed Inverter</li>
                      <li>Communicating Zoning System</li>
                      <li>10-Year Labor Warranty</li>
                      <li>Whole Home Purification</li>
                  </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Start Using This Template Now</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Get Free Access
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default HVACTieredProposal;
